import React, { useEffect, useState } from "react";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { MapPin, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { API } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const CatalogBrowsePage = () => {
  const [books, setBooks] = useState([]);
  const [query, setQuery] = useState("");
  const [city, setCity] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchBooks = async () => {
    setLoading(true);
    try {
      const params = {};
      if (query) params.q = query;
      if (city) params.city = city;
      const { data } = await axios.get(`${API}/catalog`, { params });
      setBooks(data);
    } catch {
      setBooks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBooks();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBooks();
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--background)" }}>
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-10" data-testid="catalog-browse-page">
        <h1 className="text-3xl font-bold" style={{ color: "var(--primary)" }}>
          Browse Catalog
        </h1>
        <p className="mt-2 text-gray-600">
          Shared book catalog with copies from multiple owners across cities.
        </p>

        <form onSubmit={handleSearch} className="mt-6 flex gap-3" data-testid="catalog-search-form">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by title, author, or ISBN..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-10"
              data-testid="catalog-search-input"
            />
          </div>
          <Input
            placeholder="Filter by city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="w-40"
            data-testid="catalog-city-filter"
          />
          <button
            type="submit"
            className="px-4 py-2 rounded-md text-white text-sm font-medium"
            style={{ backgroundColor: "var(--primary)" }}
            data-testid="catalog-search-btn"
          >
            Search
          </button>
        </form>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading catalog...</div>
        ) : books.length === 0 ? (
          <div className="text-center py-12 text-gray-500" data-testid="catalog-empty">
            No books found. Try a different search.
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="catalog-grid">
            {books.map((book) => (
              <Link
                key={book.id}
                to={`/catalog/${book.id}`}
                data-testid={`catalog-card-${book.id}`}
                className="group block rounded-xl border border-gray-200 bg-white overflow-hidden hover:border-gray-400 transition-colors"
              >
                <div className="aspect-[3/4] bg-gray-100 overflow-hidden">
                  {book.cover_url ? (
                    <img
                      src={book.cover_url}
                      alt={book.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                      No cover
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <div className="font-semibold text-sm line-clamp-2 leading-snug" data-testid="catalog-book-title">
                    {book.title}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">{book.author}</div>
                  <div className="flex items-center justify-between mt-2">
                    <Badge variant="secondary" className="text-xs">
                      {book.copies_count} {book.copies_count === 1 ? "copy" : "copies"}
                    </Badge>
                    {book.cities?.length > 0 && (
                      <div className="text-xs text-gray-500 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {book.cities.slice(0, 2).join(", ")}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default CatalogBrowsePage;
